import { ContextWrapper } from "@common/contexts";
import { useApp } from "@common/hooks";
import { MacroKeywords, MacroListingModel } from "@common/models";
import { MacroTargetType, MacroType } from "@common/models/Enums/MacroType";
import { AppStore, ClientStore, NotificationStoreInstance, PetDetailsStore } from "@common/stores";
import { CommunicationTemplateSearchStore } from "@common/stores/CommunicationTemplateSearchStore";
import { IQueryCacheStoreInit } from "@common/stores/QueryCacheStore";
import { useHospitalApp } from "@hospital/hooks";
//TODO need to move hospitalAppStore, examStore etc into common
//Optionally, refactor macros to have isolated values we can set from various places in apps that don't depend on specific stores.
import { HospitalAppStore } from "@hospital/stores/rootStores";
import { ExamStore } from "@hospital/views/exam";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { action, computed, makeObservable, observable } from "mobx";
import { useContext, useMemo } from "react";
import { normalizePhone } from '@common/utils/phoneUtils';

export interface IMacroStoreInit extends IQueryCacheStoreInit {
	app: AppStore;
	hospitalApp: HospitalAppStore;
	petDetails: PetDetailsStore | undefined;
	examStore: ExamStore | undefined;
	clientStore: ClientStore | undefined;
	macroTargetTypeId?: MacroTargetType;
}

export interface IKeyWordsModel {
	fullName?: string | undefined;
	petName?: string | undefined;
	firstName: string | undefined;
	lastName: string | undefined;
	petGender?: string | undefined;
	vetName?: string | undefined;
}

export class MacroStore {
	private _app: AppStore;
	private _hospitalApp: HospitalAppStore;
	private _petDetailsStore: PetDetailsStore | undefined;
	private _examStore: ExamStore | undefined;
	private _clientStore: ClientStore | undefined;
	private _templateSearchStore: CommunicationTemplateSearchStore;
	private _hospitalId: number | undefined = undefined;
	@observable private _searchText: string | undefined = undefined;
	@observable private _error: string | undefined = undefined;
	private _macroTargetTypeId: MacroTargetType | undefined = undefined;
	private _keywordsData: IKeyWordsModel | undefined = undefined;
	private _isTransientKeywords: boolean = false;

	constructor(init: IMacroStoreInit) {
		this._app = init.app;
		this._hospitalApp = init.hospitalApp;
		this._petDetailsStore = init.petDetails;
		this._examStore = init.examStore;
		this._clientStore = init.clientStore;
		this._hospitalId = this._hospitalApp.currentHospital ? this._hospitalApp.currentHospital.hospitalId : undefined;
		this._macroTargetTypeId = init.macroTargetTypeId ? init.macroTargetTypeId == MacroTargetType.All ? undefined : init.macroTargetTypeId : MacroTargetType.ExamVisit;
		this._templateSearchStore = new CommunicationTemplateSearchStore(this._hospitalId ? MacroType.Location : MacroType.Hospitals, this._hospitalId, undefined, 100000, this._macroTargetTypeId, {
			queryClient: init.queryClient,
			queryStaleTime: init.app.configuration.longClientSideCacheTimeToLive
		});
		this._templateSearchStore.updateSearchParams({ orderby: [{ field: "name", dir: "asc" }] });

		makeObservable(this, undefined, { autoBind: true });
	}

	@computed get error() {
		return this._error;
	}

	@computed get isLoading() {
		return this._templateSearchStore.isLoading;
	}

	@computed get searchText() {
		return this._searchText;
	}

	@computed get currentHospital() {
		return this._hospitalApp.currentHospital;
	}

	@computed get visit() {
		return (this._examStore && this._examStore.visit) ?? undefined;
	}

	@computed get visitClient() {
		return (this.visit && this.visit.client) ?? undefined;
	}

	@computed get client() {
		return (this._clientStore && this._clientStore.client) ?? undefined;
	}

	@computed get macroKeywords() {
		const client = this._isTransientKeywords ? this._keywordsData : this.client ?? this.visitClient;
		const clientFirstName = _.defaultTo(client && client.firstName, undefined);
		const clientLastName = _.defaultTo(client && client.lastName, undefined);
		const clientFullName = _.defaultTo(client && client.fullName, undefined);
		const petName = this._isTransientKeywords ? this._keywordsData && this._keywordsData.petName : this._petDetailsStore && this._petDetailsStore.pet && this._petDetailsStore.pet.name;
		const petGender = this._isTransientKeywords ? this._keywordsData && this._keywordsData.petGender : this._petDetailsStore && this._petDetailsStore.pet && this._petDetailsStore.pet.gender.name
		const vetName = this._isTransientKeywords ? this._keywordsData && this._keywordsData.vetName : (this.visit && this.visit.veterinarianFullName) ?? undefined;

		const macroKeyworkds: Partial<MacroKeywords> = {
			"{{hospitalname}}": this.hospitalKeyWords.hospitalName,
			"{{hospitalemail}}": this.hospitalKeyWords.hospitalEmail,
			"{{hospitalphone}}": this.hospitalKeyWords.hospitalPhone,
			"{{hospitalcity}}": this.hospitalKeyWords.hospitalCity,
			"{{hospitaladdress}}": this.hospitalKeyWords.hospitalAddress,
			"{{hospitalurl}}": this.hospitalKeyWords.hospitalUrl,
			"{{clientfirstname}}": clientFirstName,
			"{{clientlastname}}": clientLastName,
			"{{clientfullname}}": clientFullName,
			"{{petname}}": petName,
			"{{vetname}}": vetName,
			"{{todaysdate}}": this.hospitalKeyWords.timezoneName ? DateTime.now().setZone(this.hospitalKeyWords.timezoneName).toLocaleString(DateTime.DATE_SHORT) : DateTime.now().toLocaleString(DateTime.DATE_SHORT),
			"{{petgender}}": petGender,
			"{{hospitalsms}}": this.hospitalKeyWords.hospitalSms,
		};

		return new MacroKeywords(macroKeyworkds);
	}

	get hospitalKeyWords() {
		return {
			hospitalName: (this.currentHospital && this.currentHospital.name) ?? undefined,
			hospitalEmail: (this.currentHospital && this.currentHospital.email) ?? undefined,
			hospitalPhone: (this.currentHospital && this.currentHospital.phone) ? normalizePhone(this.currentHospital.phone) : undefined,
			hospitalCity: (this.currentHospital && this.currentHospital.store?.city) ?? undefined,
			hospitalAddress: (this.currentHospital && this.currentHospital.store?.address) ?? undefined,
			hospitalUrl: (this.currentHospital && this.currentHospital.url) ?? undefined,
			hospitalSms: (this.currentHospital && this.currentHospital.outboundSMSPhone) ?? undefined,
			timezoneName: (this.currentHospital && this.currentHospital.store?.timezoneName) ?? undefined
		}
	}

	@computed get macros(): MacroListingModel[] {
		if (this.isLoading) return [];

		if (!this._templateSearchStore || !this._templateSearchStore.searchResults) return [];

		if (!this._searchText) this._templateSearchStore.searchResults;

		return this._templateSearchStore.searchResults.results.filter(v => {
			return  (v.name ?? "").toLowerCase().indexOf((this.searchText ?? "").toLowerCase()) > -1 || (v.createdBy ?? "").toLowerCase().indexOf((this.searchText ?? "").toLowerCase()) > -1;
		});
	}

	@action.bound setSearchText = (searchText: string | undefined) => {
		this._searchText = searchText;
	};

	@action.bound setKeywordsData = (keyWordsData: IKeyWordsModel) => {
		this._keywordsData = keyWordsData;
	};

	@action.bound setTransientKeywords = (isTransientKeywords: boolean) => {
		this._isTransientKeywords = isTransientKeywords;
	};



	@action.bound load = () => {
		try {
			this._templateSearchStore
				.search()
				.then(() => {
					this._error = undefined;
				})
				.catch(err => {
					this._error = err;
				})
				.finally(() => {
					if (this.error) NotificationStoreInstance.error("Unable to load Note Macros.");
				});
		} catch (err) {
			this._error = err;
			NotificationStoreInstance.error("Unable to load Note Macros.");
		}
	};
}

const MacroContext = new ContextWrapper<MacroStore>();

//Dev Note: All the use* hooks here have had their contexts updated to create new stores if they aren't already set
//this prevents us from having get() errors if the text box is used in places where those contexts aren't set.
export const useMacros = (init?: { petDetails?: PetDetailsStore | undefined, examStore?: ExamStore | undefined, clientStore?: ClientStore, macroTargetTypeId?: MacroTargetType }): MacroStore => {
	const app = useApp();
	const hospitalApp = useHospitalApp();
	const queryClient = useQueryClient();
	const examStore = init?.examStore;
	const clientStore = init?.clientStore;
	const petDetails = init?.petDetails;
	const macroTargetTypeId = init?.macroTargetTypeId;

	const macroStore = useMemo(() => {
		const store = new MacroStore({
			app,
			hospitalApp,
			examStore,
			petDetails,
			macroTargetTypeId,
			queryClient,
			clientStore
		});
		MacroContext.set(store);
		return store;
	}, [app, hospitalApp, petDetails, examStore,clientStore,macroTargetTypeId, queryClient]);

	return macroStore;
};

export const useMacroContext = () => {
	const app = useApp();
	const hospitalApp = useHospitalApp();	
	const useMacroResult = useContext(MacroContext.get() ?? new MacroStore({ app: app, hospitalApp: hospitalApp, examStore: undefined, petDetails: undefined, clientStore: undefined }));
	return useMacroResult;
};
